/**
 * Derivações puras da Tela 8 (Iniciar Conversa Proativa) — wire → view model.
 *
 * Concentra TODA decisão de "como traduzir o dado real" para ser testável sem
 * React. Honestidade é regra:
 *   - D6: variáveis do template são ÍNDICES numéricos (`{{1}}`), extraídas do
 *     bodyPreview — nunca nomes inventados.
 *   - D7: canais filtrados às identidades reais (snake Prisma → kebab wire);
 *     canal não-postável degrada para fora da lista (não mostramos card que o
 *     POST rejeitaria com 422 `no_identity`).
 *   - D9: bodyPreview é opcional (contrato-piso `{name}[]`); sem ele, sem
 *     preview/variáveis — degrade honesto, não dado falso.
 */

import {
  channelLabel as rawChannelLabel,
  formatShortDate,
  maskCpf,
} from '../persons/persons-adapter';

import type { MetaCategory, ProactiveBlockedCode } from './types';
import type { ChannelKind } from '@lume/shared';

// ─── Canal: Identity.channel (snake Prisma) → ChannelKind (kebab wire) ────

const SNAKE_TO_WIRE: Record<string, ChannelKind> = {
  whatsapp_cloud_api: 'whatsapp-cloud-api',
  email: 'email',
  telegram: 'telegram',
  instagram_dm: 'instagram-dm',
  // ★ INVERTIDOS em 19/Ago/2026 (decisão do TL) — os dois canais de SESSÃO tinham as respostas
  // trocadas, e a troca era resíduo da renomeação `webchat` → `simulador`.
  //
  // `simulador` PRESENTE: é o laboratório que precisa exercitar o caminho real da proativa. A
  // versão anterior deste comentário dizia o contrário («é ferramenta de laboratório ⇒ ausente»)
  // e raciocinava invertido — laboratório que não exercita o caminho real não valida nada. O
  // harness já semeava conversa proativa em `simulador`, mas escrevendo DIRETO, porque o POST a
  // recusava: testar contornando a porta que a produção usa.
  simulador: 'simulador',
  //
  // `webchat` AUSENTE: dos três cenários do canal de produção, nenhum notifica o cliente (site
  // público e portal não têm como; o APP do tenant tem — por push — mas app não é aba de
  // navegador e provavelmente pede `ChannelKind` próprio). Enquanto esse canal não existir,
  // ausente é a única resposta honesta.
  //
  // Racional completo: docblock de `@lume/shared` `proactive-eligibility.ts` + Item 462 trava 5.
  //
  // ⚠️ Este mapa é à mão e NÃO deriva de `PROACTIVE_WIRE_CHANNELS` — o compilador não liga os
  // dois lados. Quem os liga é o TESTE de `proactive-adapter.spec.ts`, que assere a igualdade
  // nas DUAS direções (ofertado ⊆ elegível e elegível ⊆ ofertado). Apagar aquele teste
  // reabre o buraco: este mapa voltaria a poder divergir do engine em silêncio.
  // formas kebab já corretas (idempotência)
  'whatsapp-cloud-api': 'whatsapp-cloud-api',
  'instagram-dm': 'instagram-dm',
};

/** Mapeia o canal cru da Identity para o wire do POST. Desconhecido → null. */
export function channelToWire(channel: string): ChannelKind | null {
  return SNAKE_TO_WIRE[channel] ?? null;
}

const CHANNEL_LABEL: Record<ChannelKind, string> = {
  'whatsapp-cloud-api': 'WhatsApp',
  webchat: 'Webchat',
  email: 'E-mail',
  telegram: 'Telegram',
  'instagram-dm': 'Instagram',
  simulador: 'Simulador',
};

export function channelLabel(wire: ChannelKind): string {
  return CHANNEL_LABEL[wire];
}

export interface ChannelCard {
  wire: ChannelKind;
  label: string;
  identifier: string;
}

/**
 * D7: deriva os cards de canal das identidades REAIS da pessoa. Dedup por canal
 * (mantém o primeiro identificador); canal não-postável é omitido.
 */
export function availableChannels(
  identities: { channel: string; channelIdentifier: string }[],
): ChannelCard[] {
  const cards: ChannelCard[] = [];
  const seen = new Set<ChannelKind>();
  for (const id of identities) {
    const wire = channelToWire(id.channel);
    if (wire === null || seen.has(wire)) continue;
    seen.add(wire);
    cards.push({ wire, label: channelLabel(wire), identifier: id.channelIdentifier });
  }
  return cards;
}

// ─── Template: variáveis índice-numérico + preview (D6/D9) ────────────────
// Moram em `@lume/shared` (`conversations/window-24h.ts`) desde 07/Out/2026 —
// o seletor de template do app móvel é o 2º consumidor. Re-export preserva o
// caminho de import do web.

export { extractTemplateVars, renderTemplateText, splitTemplate } from '@lume/shared';
export type { TemplatePart } from '@lume/shared';

// ─── Categorias Meta — só as 3 reais (D1) ─────────────────────────────────

export interface MetaCategoryMeta {
  label: string;
  desc: string;
  /** Custo literal informativo do briefing (FEEDBACK_LIDER_SUPORTE) — estático. */
  cost: string;
}

/**
 * As 3 categorias que o v1 do backend aceita (`isCategoryAllowed`). Marketing /
 * Authentication / CTWA NÃO entram — seriam affordance falsa (não há fatia de
 * produto que as habilite). Custo é o literal informativo do mockup.
 */
export const META_CATEGORY_META: Record<MetaCategory, MetaCategoryMeta> = {
  customer_care: {
    label: 'Customer Care',
    desc: 'Atendimento dentro da janela de 24h após o cliente responder. Conversa de serviço, sem custo de template.',
    cost: '—',
  },
  service: {
    label: 'Serviço',
    desc: 'Acompanhamento de atendimento / pós-venda iniciado pela operação.',
    cost: '$',
  },
  utility: {
    label: 'Utilidade',
    desc: 'Aviso transacional: lembrete de visita, confirmação, atualização de pedido.',
    cost: '$$',
  },
};

// ─── Mensagem honesta por código 422 do POST (D8) ─────────────────────────

const BLOCKED_MESSAGE: Record<ProactiveBlockedCode, string> = {
  category_not_allowed: 'Esta categoria não está disponível no plano deste tenant.',
  adversarial_blocked: 'Bond adversarial estrito: só Customer Care é permitido para esta pessoa.',
  no_identity: 'A pessoa não tem identidade verificada no canal escolhido.',
  template_not_found: 'O template selecionado não está mais aprovado neste tenant.',
  recipient_restricted: 'Esta pessoa pediu para não ser procurada. O envio foi bloqueado.',
};

/**
 * R-G9 — a razão da restrição, na voz de quem opera. Causa desconhecida degrada para a forma
 * genérica: não inventamos um motivo que o servidor não afirmou.
 *
 * ★ Fatia R-G5 — a 3ª causa (`lgpd-opposition`) é qualitativamente diferente das outras duas:
 * é **direito exercido formalmente** (art. 18 §2), não preferência declarada. A distinção muda o
 * que o operador pode fazer a seguir — insistir por outro canal tem consequência diferente.
 */
export function recipientRestrictionMessage(cause: string | undefined): string {
  if (cause === 'opt-out-flag') {
    return 'Esta pessoa respondeu PARAR e não quer mais ser procurada.';
  }
  if (cause === 'self-restriction') {
    return 'Esta pessoa declarou uma restrição de contato.';
  }
  if (cause === 'lgpd-opposition') {
    return 'Esta pessoa exerceu oposição ao tratamento dos dados dela (LGPD art. 18 §2).';
  }
  return 'Esta pessoa pediu para não ser procurada.';
}

export function blockedMessage(code: string): string {
  return (
    BLOCKED_MESSAGE[code as ProactiveBlockedCode] ??
    'Não foi possível iniciar a conversa (envio bloqueado pelo servidor).'
  );
}

const IDENTIFIER_TYPE_LABEL: Record<string, string> = {
  cpf: 'CPF',
  cnpj: 'CNPJ',
  email: 'E-mail',
  phone: 'Telefone',
  whatsapp: 'WhatsApp',
  instagram_handle: 'Instagram',
  telegram_handle: 'Telegram',
  webchat_handle: 'Webchat',
};

/**
 * Linha de identificação do picker do Passo 1 — rótulo PT no lugar do enum cru
 * e CPF mascarado (P15, mesmo padrão da tela Pessoas). Sem identificador,
 * cai para a contagem de canais com plural correto.
 */
export function personIdentifierLine(p: {
  primaryIdentifierType: string | null;
  primaryIdentifierValue: string | null;
  identityCount: number;
}): string {
  if (p.primaryIdentifierValue !== null && p.primaryIdentifierValue !== '') {
    const type = p.primaryIdentifierType ?? '';
    const label = IDENTIFIER_TYPE_LABEL[type] ?? type;
    const value = type === 'cpf' ? maskCpf(p.primaryIdentifierValue) : p.primaryIdentifierValue;
    return label !== '' ? `${label} ${value}` : value;
  }
  return `${p.identityCount} ${p.identityCount === 1 ? 'canal' : 'canais'}`;
}

// ─── N2-17: o que DISTINGUE duas pessoas de mesmo nome no seletor ─────────

const IDENTIFIER_PREFIX_CHARS = 12;

/**
 * Identificador de canal mascarado para a linha do seletor (P15 — privacidade desde o desenho,
 * mesma régua do `maskCpf`). Regras, na ordem:
 *   - 8+ dígitos (telefone WhatsApp/Telegram, id numérico) ⇒ só os 4 últimos: `•••• 1234`;
 *   - e-mail ⇒ inicial + domínio: `m***@exemplo.com`;
 *   - o resto (handle, id do simulador) ⇒ prefixo de 12 chars + `…` se for mais longo.
 * O canal entra como parâmetro para a regra poder divergir por canal no futuro; hoje a forma do
 * valor decide, porque o mesmo canal carrega formas diferentes (Telegram: número OU @handle).
 */
export function maskChannelIdentifier(_channel: string, value: string): string {
  const digits = value.replace(/\D/g, '');
  const onlyPhoneChars = /^[\d\s()+.-]+$/.test(value);
  if (onlyPhoneChars && digits.length >= 8) return `•••• ${digits.slice(-4)}`;
  const at = value.indexOf('@');
  if (at > 0) return `${value.charAt(0)}***${value.slice(at)}`;
  if (value.length > IDENTIFIER_PREFIX_CHARS) return `${value.slice(0, IDENTIFIER_PREFIX_CHARS)}…`;
  return value;
}

/**
 * Linha do seletor do Passo 1 que DISTINGUE homônimos (N2-17, noturna 2 de 05/Set/2026: 8 botões
 * «Cliente do Simulador · 1 canal» idênticos). Ordem: identificador primário (como
 * `personIdentifierLine`) · canal + identificador mascarado (até 2) · «última conversa <data>».
 * Sem `identities` no payload (engine antigo) cai no rótulo de contagem — nunca inventa canal.
 */
export function personDistinguishingLine(p: {
  primaryIdentifierType: string | null;
  primaryIdentifierValue: string | null;
  identityCount: number;
  identities?: { channel: string; channelIdentifier: string }[];
  lastConversationAt?: string | null;
}): string {
  const parts: string[] = [];
  const hasPrimary = p.primaryIdentifierValue !== null && p.primaryIdentifierValue !== '';
  if (hasPrimary) parts.push(personIdentifierLine(p));
  const identities = p.identities ?? [];
  for (const id of identities.slice(0, 2)) {
    parts.push(
      `${rawChannelLabel(id.channel)} · ${maskChannelIdentifier(id.channel, id.channelIdentifier)}`,
    );
  }
  if (p.lastConversationAt != null && p.lastConversationAt !== '') {
    parts.push(`última conversa ${formatShortDate(p.lastConversationAt)}`);
  }
  if (parts.length === 0) return personIdentifierLine(p);
  return parts.join(' · ');
}
